import {
  DetailModel,
  FieldModel,
  ProgramBenchmarkName,
  ProgramTask,
  TaskDataTraitStatus,
} from '@cibo/core'
import { GridCellParams, MuiEvent, useGridApiRef } from '@mui/x-data-grid-pro'
import { useSnackbar } from 'notistack'
import { KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useProgramEngagementContext } from '../../../../context'
import { useUpdateEngagementTaskData } from '../../../../queries'
import { adjustDetailDatesForYear, pickDetailValue } from '../../models/TaskDataModel'

export type CopiedData =
  | { type: 'cell'; fieldId: string; traitId: string; year: number; detail: DetailModel }
  | {
      type: 'row'
      fieldId: string
      traitId: string
      years: Array<{ year: number; detail: DetailModel }>
    }
  | {
      type: 'column'
      fieldId: string
      year: number
      traits: Array<{ traitId: string; detail: DetailModel }>
    }

const DATE_FIELD_LABELS: Record<string, string> = {
  plantedAt: 'planted at',
  harvestedAt: 'harvested at',
  terminatedAt: 'terminated at',
  lossAt: 'loss at',
  date: 'date',
  startDate: 'start date',
  endDate: 'end date',
}

function formatDateFieldLabel(field: string): string {
  return (
    DATE_FIELD_LABELS[field] ??
    field
      .replace(/([A-Z])/g, ' $1')
      .toLowerCase()
      .trim()
  )
}

export function resolveDetailValueKey(
  detail: object,
  configuredKey: 'input' | 'result' | 'value' | undefined
): 'input' | 'result' | 'value' {
  if (configuredKey) return configuredKey
  const d = detail as Record<string, unknown>
  if (d.input !== undefined) return 'input'
  if (d.result !== undefined) return 'result'
  if (d.value !== undefined) return 'value'
  return 'input'
}

function pickAndAdjustDetailValue(
  detail: object,
  detailValueKey: 'input' | 'result' | 'value' | undefined,
  sourceYear: number,
  targetYear: number
): { payload: Record<string, unknown>; datesChanged: boolean; changedFields: string[] } {
  const resolved = resolveDetailValueKey(detail, detailValueKey)
  const picked = pickDetailValue(detail, resolved)
  const dvk = Object.keys(picked)[0]
  if (!dvk || picked[dvk] == null || sourceYear === targetYear) {
    return { payload: picked, datesChanged: false, changedFields: [] }
  }
  const { adjusted, datesChanged, changedFields } = adjustDetailDatesForYear(
    picked[dvk],
    sourceYear,
    targetYear
  )
  return { payload: { [dvk]: adjusted }, datesChanged, changedFields }
}

const removePastingKeys = (keys: string[]) => (prev: Set<string>) => {
  const next = new Set(prev)
  keys.forEach(k => next.delete(k))
  return next
}

function hasDetailValue(detail: DetailModel): boolean {
  return (
    detail.input !== undefined || detail.result !== undefined || (detail as any).value !== undefined
  )
}

interface UseCopyPasteParams {
  task: ProgramTask<{ benchmarkName: ProgramBenchmarkName; traits: TaskDataTraitStatus[] }>
  selectedFieldId: string | null
  selectedField: FieldModel | undefined
  years: string[]
  rows: Array<{ traitId: string }>
  gridApiRef: ReturnType<typeof useGridApiRef>
}

export function useCopyPaste({
  task,
  selectedFieldId,
  selectedField,
  years,
  rows,
  gridApiRef,
}: UseCopyPasteParams) {
  const { engagement, taskData } = useProgramEngagementContext()
  const { t } = useTranslation(['traits', '@cibo/programs/TaskEditorTable'])
  const { enqueueSnackbar } = useSnackbar()
  const columnKeyHandlerRef = useRef<(e: globalThis.KeyboardEvent) => void>()

  const [copied, setCopied] = useState<CopiedData | null>(null)
  const [pastingCells, setPastingCells] = useState<Set<string>>(new Set())

  const updateEngagementTaskData = useUpdateEngagementTaskData({
    engagementId: engagement.data?.id,
    taskId: task.id,
  })

  const getDetailValueKey = (traitId: string): 'input' | 'result' | 'value' | undefined =>
    task.rule.params?.traits?.find(t => t.traitId === traitId)?.detailValueKey ??
    taskData.data?.getTraitStatus(traitId)?.detailValueKey

  const showDateAdjustedSnackbar = (
    adjustments: Array<{ traitId: string; changedFields: string[] }>,
    targetYear: number
  ) => {
    if (!adjustments.length) return
    const allFields = [...new Set(adjustments.flatMap(a => a.changedFields))]
    const allTraits = adjustments.map(a => t(a.traitId, { ns: 'traits' }).toLowerCase())
    const fieldLabels = allFields.map(formatDateFieldLabel).join(' and ')
    const traitLabel = allTraits.join(', ')
    enqueueSnackbar(
      t('pasteDatesAdjusted', {
        ns: '@cibo/programs/TaskEditorTable',
        fields: fieldLabels,
        trait: traitLabel,
        year: targetYear,
      }),
      { variant: 'info' }
    )
  }

  const pasteColumn = (
    copied: Extract<CopiedData, { type: 'column' }>,
    targetYear: number,
    field: FieldModel
  ) => {
    const immutableCount = copied.traits.filter(
      ({ traitId }) => field.findDetail({ traitId, year: targetYear })?.immutable
    ).length
    const dateAdjustments: Array<{ traitId: string; changedFields: string[] }> = []
    const details = copied.traits
      .filter(({ traitId }) => !field.findDetail({ traitId, year: targetYear })?.immutable)
      .map(({ traitId, detail }) => {
        const { payload, datesChanged, changedFields } = pickAndAdjustDetailValue(
          detail,
          getDetailValueKey(traitId),
          copied.year,
          targetYear
        )
        if (datesChanged) {
          dateAdjustments.push({ traitId, changedFields })
        }
        return { traitId, year: targetYear, ...payload }
      }) as any[]

    if (!details.length) {
      enqueueSnackbar(t('pasteAllLocked', { ns: '@cibo/programs/TaskEditorTable' }), {
        variant: 'error',
      })
      return
    }
    if (immutableCount > 0) {
      enqueueSnackbar(
        t('pastePartialLocked', { count: immutableCount, ns: '@cibo/programs/TaskEditorTable' }),
        { variant: 'warning' }
      )
    }

    const keys = (details as Array<{ traitId: string; year: number }>).map(
      d => `${d.traitId}:${d.year}`
    )
    setPastingCells(prev => {
      const next = new Set(prev)
      keys.forEach(k => next.add(k))
      return next
    })
    updateEngagementTaskData
      .mutateAsync({
        updates: { fieldDetails: [{ resourceId: field.resourceId, details }] },
      })
      .then(() => {
        copied.traits.forEach(({ traitId, detail }) => {
          gridApiRef.current?.updateRows([{ id: traitId, [String(targetYear)]: detail }])
        })
        setPastingCells(removePastingKeys(keys))
        showDateAdjustedSnackbar(dateAdjustments, targetYear)
      })
      .catch((err: any) => {
        setPastingCells(removePastingKeys(keys))
        enqueueSnackbar(
          err?.response.data.message || t('pasteError', { ns: '@cibo/programs/TaskEditorTable' }),
          { variant: 'error' }
        )
      })
  }

  const handleCellKeyDown = useCallback(
    // eslint-disable-next-line visual/complexity
    (params: GridCellParams, event: MuiEvent<KeyboardEvent>) => {
      const traitId = String(params.id)
      const isRowHeader = params.field === 'traitId'
      const year = isRowHeader ? 0 : Number(params.field)

      if ((event.ctrlKey || event.metaKey) && event.key === 'c') {
        event.defaultMuiPrevented = true
        if (isRowHeader) {
          const yearDetails = years
            .map(y => ({
              year: Number(y),
              detail: selectedField?.findDetail({ traitId, year: Number(y) }),
            }))
            .filter((x): x is { year: number; detail: DetailModel } => x.detail != null)
          if (yearDetails.length)
            setCopied({ type: 'row', fieldId: selectedFieldId ?? '', traitId, years: yearDetails })
        } else {
          const detail = selectedField?.findDetail({ traitId, year })
          if (detail && hasDetailValue(detail as DetailModel)) {
            setCopied({
              type: 'cell',
              fieldId: selectedFieldId ?? '',
              traitId,
              year,
              detail: detail as DetailModel,
            })
          }
        }
        return
      }

      if ((event.ctrlKey || event.metaKey) && event.key === 'v') {
        event.defaultMuiPrevented = true
        if (!copied || !selectedField) return

        if (isRowHeader) {
          if (copied.type !== 'row') return
          if (copied.traitId !== traitId) {
            enqueueSnackbar(t('pasteMismatchedTraits', { ns: '@cibo/programs/TaskEditorTable' }), {
              variant: 'error',
            })
            return
          }
          const details = copied.years
            .filter(({ year }) => !selectedField.findDetail({ traitId, year })?.immutable)
            .map(({ year, detail }) => ({
              traitId,
              year,
              ...pickDetailValue(detail, resolveDetailValueKey(detail, getDetailValueKey(traitId))),
            })) as any[]
          if (!details.length) {
            enqueueSnackbar(t('pasteAllLocked', { ns: '@cibo/programs/TaskEditorTable' }), {
              variant: 'error',
            })
            return
          }
          const keys = (details as Array<{ traitId: string; year: number }>).map(
            d => `${d.traitId}:${d.year}`
          )
          setPastingCells(prev => {
            const next = new Set(prev)
            keys.forEach(k => next.add(k))
            return next
          })
          updateEngagementTaskData
            .mutateAsync({
              updates: { fieldDetails: [{ resourceId: selectedField.resourceId, details }] },
            })
            .then(() => {
              copied.years.forEach(({ year, detail }) => {
                gridApiRef.current?.updateRows([{ id: traitId, [String(year)]: detail }])
              })
              setPastingCells(removePastingKeys(keys))
            })
            .catch((err: any) => {
              setPastingCells(removePastingKeys(keys))
              enqueueSnackbar(
                err?.response.data.message ||
                  t('pasteError', { ns: '@cibo/programs/TaskEditorTable' }),
                { variant: 'error' }
              )
            })
        } else {
          if (copied.type === 'column') {
            pasteColumn(copied, year, selectedField)
            return
          }
          if (copied.type !== 'cell') return
          if (copied.traitId !== traitId) {
            enqueueSnackbar(t('pasteMismatchedTraits', { ns: '@cibo/programs/TaskEditorTable' }), {
              variant: 'error',
            })
            return
          }
          if (selectedField.findDetail({ traitId, year })?.immutable) {
            enqueueSnackbar(
              t('cannotPasteLocked', { ns: '@cibo/programs/TaskEditorTable', count: 1 }),
              { variant: 'error' }
            )
            return
          }
          const {
            payload: cellPayload,
            datesChanged: cellDatesChanged,
            changedFields: cellChangedFields,
          } = pickAndAdjustDetailValue(copied.detail, getDetailValueKey(traitId), copied.year, year)
          const key = `${traitId}:${year}`
          setPastingCells(prev => new Set(prev).add(key))
          updateEngagementTaskData
            .mutateAsync({
              updates: {
                fieldDetails: [
                  {
                    resourceId: selectedField.resourceId,
                    details: [{ traitId, year, ...cellPayload }] as any[],
                  },
                ],
              },
            })
            .then(() => {
              gridApiRef.current?.updateRows([{ id: traitId, [params.field]: copied.detail }])
              setPastingCells(removePastingKeys([key]))
              if (cellDatesChanged) {
                showDateAdjustedSnackbar([{ traitId, changedFields: cellChangedFields }], year)
              }
            })
            .catch((err: any) => {
              setPastingCells(removePastingKeys([key]))
              enqueueSnackbar(
                err?.response.data.message ||
                  t('pasteError', { ns: '@cibo/programs/TaskEditorTable' }),
                { variant: 'error' }
              )
            })
        }
        return
      }

      if (event.key === 'Escape') setCopied(null)
    },
    [
      copied,
      selectedField,
      selectedFieldId,
      updateEngagementTaskData,
      gridApiRef,
      years,
      enqueueSnackbar,
    ]
  )

  // Column header key handler — assigned on every render for fresh closure
  columnKeyHandlerRef.current = (e: globalThis.KeyboardEvent) => {
    const headerEl = (document.activeElement as HTMLElement)?.closest?.(
      '[role="columnheader"][data-field]'
    )
    const field = headerEl?.getAttribute('data-field')
    if (!field || field === 'traitId') return
    const year = Number(field)

    if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
      e.preventDefault()
      const traits = rows
        .map(r => ({
          traitId: r.traitId,
          detail: selectedField?.findDetail({ traitId: r.traitId, year }),
        }))
        .filter(
          (x): x is { traitId: string; detail: DetailModel } =>
            x.detail != null && hasDetailValue(x.detail as DetailModel)
        )
      if (traits.length) setCopied({ type: 'column', fieldId: selectedFieldId ?? '', year, traits })
      return
    }

    if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
      e.preventDefault()
      if (!copied || copied.type !== 'column' || !selectedField) return
      pasteColumn(copied, year, selectedField)
      return
    }

    if (e.key === 'Escape') setCopied(null)
  }

  useEffect(() => {
    const handler = (e: globalThis.KeyboardEvent) => columnKeyHandlerRef.current?.(e)
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  const getCellClassName = useCallback(
    (params: GridCellParams) => {
      if (!copied) return ''
      const id = String(params.id)
      const field = params.field
      const isSourceField = selectedFieldId === copied.fieldId
      if (copied.type === 'cell') {
        if (id !== copied.traitId || Number(field) !== copied.year) return ''
        return isSourceField ? 'cell--copied' : 'cell--paste-target'
      }
      if (copied.type === 'row') {
        if (id !== copied.traitId || field === 'traitId') return ''
        if (!copied.years.some(y => y.year === Number(field))) return ''
        return isSourceField ? 'cell--copied' : 'cell--paste-target'
      }
      if (copied.type === 'column') {
        if (field === 'traitId' || Number(field) !== copied.year) return ''
        if (!copied.traits.some(t => t.traitId === id)) return ''
        return isSourceField ? 'cell--copied' : 'cell--paste-target'
      }
      return ''
    },
    [copied, selectedFieldId]
  )

  return {
    copied,
    pastingCells,
    handleCellKeyDown,
    getCellClassName,
  }
}
